import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import { Stack, Typography, useMediaQuery, Link, Button } from '@mui/material'
import ReceiptIcon from '@mui/icons-material/Receipt'

import { SortOrder } from 'service-api/src/types'

import { HeadCell, Order, SharedTable } from 'table'
import { membershipCycle } from '@shared/memberships'
import {
  getBenefitOfferLabel,
  getBCRMembershipCycleForDate,
  formatCycleLabel,
  getUniqueBenefits,
  getYears,
  getBenefitOffers,
  getCycles,
} from './helper'
import { i18WithParams as t } from '@shared/locales'
import { formatISOStringToMonthShort, getTzDateInUtc } from '@shared/dates'
import { BenefitConsumptionTableFilters } from './benefit_consumption_table_filters'
import { format } from 'date-fns'
import { BenefitConsumption } from 'membership/benefit_consumption'
import {
  TBenefitConsumptionRecord,
  TBenefitConsumptionTableFilters,
  TMembershipCycle,
  TLocationUserProfileMembership,
} from './types'
import { Today } from '@mui/icons-material'

export interface RequestOptions {
  sort?: string
  sortOrder?: SortOrder
  locationUserProfileId?: string
  invoiceStartDate?: string
  limit?: number
  status?: string
  nextToken?: string | null
  offerId?: string
  locationUserProfileMembershipId?: string
  enabled?: boolean
}

const BENEFIT = 'benefitId'
const MEMBERSHIP = 'membershipId'
const LOCATION = 'location'
const VISIT_DATE = 'invoiceStartDate'
const MEMBERSHIP_CYCLE = 'membershipCycle'
const ACTIONS = 'actions'

const DATE_FILTER_FORMAT = 'yyyy-MM-dd'
const ROWS_PER_PAGE = [50, 100]
const LIMIT = 50
const DEFAULT_FETCH_OPTIONS: RequestOptions = {
  sort: VISIT_DATE,
  sortOrder: SortOrder.DESC,
  limit: LIMIT,
  status: 'ACTIVE',
}

export const BenefitConsumptionTable = ({
  locationUserProfileMembership,
  timezone,
  data,
  isLoading,
  onOptionsChange,
  onLoadMore,
  inlineFilters,
  isSortable,
  isAdmin,
  nextToken,
  invoicePath,
  hasSharedCRM,
  invoiceFilterDates,
  setIsInitialLoaded,
  isInitialLoaded,
}: {
  timezone: string
  locationUserProfileMembership?: TLocationUserProfileMembership
  data?: TBenefitConsumptionRecord[]
  isLoading?: boolean
  onOptionsChange?: (options: RequestOptions) => void
  onLoadMore?: () => void
  inlineFilters?: boolean
  isSortable?: boolean
  isAdmin?: boolean
  nextToken?: string | null
  invoicePath?: (benefitConsumptionRecord: TBenefitConsumptionRecord) => string
  hasSharedCRM?: boolean
  invoiceFilterDates?: { startDate: string; endDate: string } | undefined
  setIsInitialLoaded?: (value: boolean) => void
  isInitialLoaded?: boolean
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const [offers, setOffers] = useState<Map<string, string>>(new Map())
  const [years, setYears] = useState<string[]>([])
  const [cycles, setCycles] = useState<Map<string, TMembershipCycle[]>>(new Map())
  const [baseCycle, setBaseCycle] = useState<TMembershipCycle | undefined>(undefined)
  const [currentCycle, setCurrentCycle] = useState<TMembershipCycle | undefined>(undefined)
  const [currentYear, setCurrentYear] = useState<string | undefined>(undefined)

  const [hideConsumptionNumbers, setHideConsumptionNumbers] = useState(false)

  const isMembershipCancelled = locationUserProfileMembership?.subStatus === 'CANCELED'

  // sets filter data
  const [searchParams] = useSearchParams()
  const targetCycle = searchParams.get('membershipCycle')
  useEffect(() => {
    if (!locationUserProfileMembership || !invoiceFilterDates || isInitialLoaded) return
    const benefitOffers = getBenefitOffers(locationUserProfileMembership)
    const years = getYears(locationUserProfileMembership)
    const cycles = getCycles(locationUserProfileMembership, invoiceFilterDates, timezone)
    const baseCycle = membershipCycle(
      locationUserProfileMembership,
      locationUserProfileMembership?.locationMembership?.membership?.billingCycle,
      timezone
    ) as TMembershipCycle
    let currentCycle = baseCycle

    // if cycle is present in query params, override current cycle
    if (targetCycle) {
      const [startDate, endDate] = targetCycle.split('_')
      const year = startDate.split('-')[0]

      if (!year || !startDate || !endDate) {
        currentCycle = membershipCycle(
          locationUserProfileMembership,
          locationUserProfileMembership?.locationMembership?.membership?.billingCycle,
          timezone
        ) as TMembershipCycle
      } else {
        const formattedStartDate = getTzDateInUtc(startDate)
        const formattedEndDate = getTzDateInUtc(endDate)

        const queryCycle = cycles
          .get(year)
          ?.find(
            (cycle) =>
              formatCycleLabel(cycle) === formatCycleLabel({ startDate: formattedStartDate, endDate: formattedEndDate })
          )
        currentCycle = queryCycle || currentCycle
      }
    }
    const currentYear = currentCycle?.startDate.getFullYear().toString()
    setOffers(benefitOffers)
    setYears(years)
    setCurrentYear(currentYear)
    setCycles(cycles)
    setCurrentCycle(currentCycle)
    setBaseCycle(baseCycle)
    setIsInitialLoaded?.(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationUserProfileMembership, invoiceFilterDates, timezone])

  useEffect(() => {
    if (isInitialLoaded && currentYear && currentCycle && onOptionsChange) {
      applyFilters({ benefitOffer: t('global.label.all'), year: currentYear, cycle: currentCycle })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentYear, currentCycle, onOptionsChange, isInitialLoaded])

  const applyFilters = (filters: TBenefitConsumptionTableFilters) => {
    const options: RequestOptions = {
      ...DEFAULT_FETCH_OPTIONS,
      locationUserProfileMembershipId: locationUserProfileMembership?.id,
    }

    if (filters?.cycle) {
      options.invoiceStartDate = `gte_${format(filters.cycle.startDate, DATE_FILTER_FORMAT)}_lte_${format(filters.cycle.endDate, DATE_FILTER_FORMAT)}`
      setHideConsumptionNumbers(false)
    } else if (invoiceFilterDates?.startDate && invoiceFilterDates?.endDate) {
      options.invoiceStartDate = `gte_${invoiceFilterDates?.startDate}_lte_${invoiceFilterDates?.endDate}`
      setHideConsumptionNumbers(true)
    }

    if (filters?.benefitOffer && filters.benefitOffer !== t('global.label.all')) {
      options.offerId = filters.benefitOffer
    } else {
      options.offerId = undefined
    }

    onOptionsChange?.(options)
  }

  const clearFilters = () => {
    const options: RequestOptions = {
      ...DEFAULT_FETCH_OPTIONS,
      locationUserProfileMembershipId: locationUserProfileMembership?.id,
      invoiceStartDate: undefined,
      offerId: undefined,
    }

    if (currentCycle) {
      options.invoiceStartDate = `gte_${format(currentCycle.startDate, DATE_FILTER_FORMAT)}_lte_${format(currentCycle.endDate, DATE_FILTER_FORMAT)}`
      setHideConsumptionNumbers(false)
    }

    onOptionsChange?.(options)
  }

  const handleSorting = (sort: string, order: Order) => {
    const options: RequestOptions = {
      ...DEFAULT_FETCH_OPTIONS,
      locationUserProfileMembershipId: locationUserProfileMembership?.id,
      sort,
      sortOrder: order as SortOrder,
    }
    onOptionsChange?.(options)
  }

  const handleRowsPerPageChange = (rowsPerPage: number) => {
    const options: RequestOptions = {
      ...DEFAULT_FETCH_OPTIONS,
      locationUserProfileMembershipId: locationUserProfileMembership?.id,
      limit: rowsPerPage,
    }
    onOptionsChange?.(options)
  }

  const loadMore = () => {
    onLoadMore?.()
  }

  const currentBenefits = useMemo(() => getUniqueBenefits(data), [data])

  const headCells = (): HeadCell[] => {
    return [
      {
        id: BENEFIT,
        label: t('global.label.benefit'),
        width: '25%',
        sortable: isSortable,
      },
      ...(isAdmin
        ? [
            {
              id: MEMBERSHIP,
              label: t('global.label.membership'),
              width: '25%',
              sortable: false,
            },
            ...(hasSharedCRM
              ? [
                  {
                    id: LOCATION,
                    label: t('global.label.location'),
                    width: '25%',
                    sortable: false,
                  },
                ]
              : []),
          ]
        : []),
      {
        id: VISIT_DATE,
        label: t('membership.benefit.visitDate'),
        width: '25%',
        sortable: isSortable,
      },
      {
        id: MEMBERSHIP_CYCLE,
        label: t('membership.benefit.membershipCycle'),
        width: '25%',
        sortable: false,
      },
      {
        id: ACTIONS,
        label: t('global.label.actions'),
        align: 'right',
        width: '25%',
        sortable: false,
      },
    ]
  }

  const getRenderRows = (benefitConsumptionRecord: TBenefitConsumptionRecord) => {
    const { benefit, offerId } = benefitConsumptionRecord
    const cycleDates = getBCRMembershipCycleForDate(benefitConsumptionRecord)

    const isInCurrentCycle = formatCycleLabel(cycleDates, timezone) === formatCycleLabel(currentCycle, timezone)

    const rowData: Record<string, React.ReactNode> = {
      [BENEFIT]: (
        <Typography variant="body2bold">
          {getBenefitOfferLabel(benefit!, offerId || '')} {t('membership.benefit.discount.small')}
        </Typography>
      ),
      [VISIT_DATE]: (
        <Typography variant="body2bold">
          {formatISOStringToMonthShort(benefitConsumptionRecord.invoiceStartDate!)}
        </Typography>
      ),
      [MEMBERSHIP_CYCLE]: (
        <Stack direction="row" spacing={1} alignItems="center">
          {isInCurrentCycle && <Today fontSize="small" color="success" />}
          <Typography variant="body2bold">{formatCycleLabel(cycleDates, timezone, 'MMM d, yyyy')}</Typography>
        </Stack>
      ),
      [ACTIONS]: (
        <Stack direction="row" spacing={1} justifyContent="right">
          <Link
            href={invoicePath?.(benefitConsumptionRecord)}
            component={Button}
            sx={{ fontSize: 14, marginTop: '3px', minWidth: 28, textDecoration: 'none' }}
          >
            <ReceiptIcon />
          </Link>
        </Stack>
      ),
    }

    if (isAdmin) {
      rowData[MEMBERSHIP] = (
        <Typography variant="body2bold">
          {benefitConsumptionRecord.locationUserProfileMembership?.membership?.displayName || ''}
        </Typography>
      )
      if (hasSharedCRM) {
        rowData[LOCATION] = (
          <Typography variant="body2bold">
            {benefitConsumptionRecord.location?.displayName || benefitConsumptionRecord.location?.name || ''}
          </Typography>
        )
      }
    }

    return rowData
  }

  return (
    <Stack
      sx={{
        border: '1px solid #E0E0E0',
        borderRadius: '8px',
      }}
    >
      <Stack
        sx={{
          p: 3,
          backgroundColor: theme.palette.info.shade10,
        }}
      >
        <Typography variant="h5">{t('membership.benefit.tableLabel')}</Typography>
        {!inlineFilters && (
          <Stack>
            <BenefitConsumptionTableFilters
              benefitOffers={offers}
              years={years}
              cycles={cycles}
              baseCycle={baseCycle}
              defaultCycle={currentCycle}
              defaultYear={currentYear}
              onApplyFilters={applyFilters}
              onClearFilters={clearFilters}
              hideBenefitFilter={isMembershipCancelled}
            />
          </Stack>
        )}
      </Stack>
      <Stack py={1} px={3}>
        <BenefitConsumption
          hideConsumptionNumbers={hideConsumptionNumbers}
          currentBenefitsUsage={data}
          currentBenefits={currentBenefits}
          isLoading={isLoading}
        />
        <SharedTable
          containerStyle={{
            '&&&': {
              padding: 0,
            },
          }}
          component={Stack}
          rows={data?.map(getRenderRows) || []}
          head={headCells()}
          defaultOrder="desc"
          defaultOrderBy={VISIT_DATE}
          isLoading={isLoading}
          rowsPerPageConfig={ROWS_PER_PAGE}
          onChangeRowsPerPage={handleRowsPerPageChange}
          hasMore={!!nextToken}
          loadMore={loadMore}
          emptyTableMessage={t('global.label.noData')}
          onSort={handleSorting}
          isScrollable={isMobile}
          dropdownFilters={
            inlineFilters ? (
              <BenefitConsumptionTableFilters
                benefitOffers={offers}
                years={years}
                cycles={cycles}
                baseCycle={baseCycle}
                defaultCycle={currentCycle}
                defaultYear={currentYear}
                onApplyFilters={applyFilters}
                onClearFilters={clearFilters}
                hideBenefitFilter={isMembershipCancelled}
                inline={true}
                isLoading={isLoading}
              />
            ) : undefined
          }
          dropdownFiltersStyle={inlineFilters ? { backgroundColor: 'transparent', mt: 2, mb: 3 } : undefined}
        />
      </Stack>
    </Stack>
  )
}
